import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

// ── Styled components ─────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// <ol> so each notification is semantically a list item with meaningful
// document structure. aria-live="polite" on the list means new items are
// announced after the user finishes their current interaction (appropriate
// for non-critical notices). aria-label names the region for screen readers.
const ToastStack = styled('ol')`
  position: fixed;
  bottom: var(--spacing-6);
  right: var(--spacing-6);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  pointer-events: none;
  list-style: none;
  margin: 0;
  padding: 0;
`;

// Each toast renders as a tinted "chip" using the chip token pair from the
// theme. The pair (--color-bg-* + --color-chip-text-*) is mode-aware and
// clears AAA in both light and dark mode without any runtime check.
//
// Previously: solid saturated background + hardcoded white text — failed AAA
// in dark mode (white-on-warning ≈ 1.7:1) and was illegible for warning toasts
// in light mode too.
const ToastItem = styled('li')<{ $type: ToastType }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-2);
  padding: 12px 16px 12px 20px;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-family: var(--font-body);
  max-width: 380px;
  pointer-events: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  background: ${({ $type }) => `var(--color-bg-${$type})`};
  color: ${({ $type }) => `var(--color-chip-text-${$type})`};
  border: 1px solid ${({ $type }) => `var(--color-chip-text-${$type})`};
  animation: ${slideIn} var(--duration-entering) var(--duration-shorter) both;
`;

const ToastMessage = styled('span')`
  flex: 1;
  line-height: 1.4;
  word-wrap: break-word;
`;

const ToastDismissButton = styled('button')`
  background: none;
  border: 0;
  padding: 2px;
  margin: -2px;
  cursor: pointer;
  color: inherit;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.75;
  transition: opacity var(--duration-shortest) ease;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    opacity: 1;
  }
`;

// ── Context ───────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Calculate display duration based on message length. Per WCAG 2.2.1
 * Timing Adjustable, short messages get a 5s minimum and longer messages
 * scale up so users have time to read. Hovering pauses the timer (see below)
 * so this is a baseline rather than a strict cap.
 */
const computeDuration = (message: string): number => {
  const base = 5000;
  const perChar = 50;
  const max = 12000;
  return Math.min(base + message.length * perChar, max);
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Keep one timeout per toast id so we can pause/resume on hover.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Remaining ms when a toast is paused — restored when hover ends.
  const remainingRef = useRef<Map<string, number>>(new Map());
  const startedAtRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(id);
    remainingRef.current.delete(id);
    startedAtRef.current.delete(id);
  }, []);

  const startTimer = useCallback(
    (id: string, ms: number) => {
      startedAtRef.current.set(id, Date.now());
      remainingRef.current.set(id, ms);
      const timer = setTimeout(() => dismissToast(id), ms);
      timersRef.current.set(id, timer);
    },
    [dismissToast],
  );

  const pauseTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (!timer) return;
    clearTimeout(timer);
    timersRef.current.delete(id);
    const startedAt = startedAtRef.current.get(id) ?? Date.now();
    const remaining = remainingRef.current.get(id) ?? 0;
    const elapsed = Date.now() - startedAt;
    remainingRef.current.set(id, Math.max(0, remaining - elapsed));
  }, []);

  const resumeTimer = useCallback(
    (id: string) => {
      // Don't double-start if already running.
      if (timersRef.current.has(id)) return;
      const remaining = remainingRef.current.get(id) ?? 5000;
      startTimer(id, remaining);
    },
    [startTimer],
  );

  const showToast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = `${Date.now()}-${Math.random()}`;
      setToasts((prev) => [...prev, { id, message, type }]);
      startTimer(id, computeDuration(message));
    },
    [startTimer],
  );

  const success = useCallback((msg: string) => showToast(msg, 'success'), [showToast]);
  const error = useCallback((msg: string) => showToast(msg, 'error'), [showToast]);
  const info = useCallback((msg: string) => showToast(msg, 'info'), [showToast]);
  const warning = useCallback((msg: string) => showToast(msg, 'warning'), [showToast]);

  // Clean up all timers on unmount.
  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      {toasts.length > 0 && (
        <ToastStack aria-live="polite" aria-label="Notifications">
          {toasts.map((t) => (
            <ToastItem
              key={t.id}
              $type={t.type}
              // Errors use role="alert" (assertive) so they interrupt immediately.
              // All other toast types use role="status" (polite). The aria-live on
              // the parent <ol> handles announcement; role here is for AT that
              // index by role rather than by live region.
              role={t.type === 'error' ? 'alert' : 'status'}
              onMouseEnter={() => pauseTimer(t.id)}
              onMouseLeave={() => resumeTimer(t.id)}
              onFocus={() => pauseTimer(t.id)}
              onBlur={() => resumeTimer(t.id)}
            >
              <ToastMessage>{t.message}</ToastMessage>
              <ToastDismissButton
                type="button"
                onClick={() => dismissToast(t.id)}
                aria-label="Dismiss notification"
              >
                <CloseIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              </ToastDismissButton>
            </ToastItem>
          ))}
        </ToastStack>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
